import { Injectable } from '@angular/core';
import { ApiService, GlobalDataService } from '../common';
import { API_ENDPOINT, GLOBAL_DATA_KEY } from '@core/configs';
import { BathRoom } from '@core/models/models';
import { InsertOrUpdateBathRoomModel, BathMaster, BathMasterItem, BathDais, DeleteBathroom, BathDaiUpdate, BathDaiMove, BathDailyItem, DAISWithKeys, BathDaiChangePreviewRequest } from '@core/models/models/bathe.model';
import { forkJoin, map } from 'rxjs';
import { BathDaiRequestModel, BathDailyRequestModel } from '@core/models/request/bathe-request.model';

@Injectable({
  providedIn: 'root',
})
export class BathRoomSettingService {
  constructor(
    private readonly http: ApiService,
    private readonly globalDataService: GlobalDataService
  ) {
  }

  /**
   * Map a bathroom response's raw dai list into BathDais, dropping soft-deleted dais.
   *
   * A dai kept in a room's stored list for history but no longer a live member of that room
   * (e.g. moved to another room) carries `delflg`. The office-bathrooms endpoint already omits
   * these from its DaiTable CSV, so the rest of the app treats a room's BATHDAIS as its live
   * roster. Filtering here keeps every endpoint that returns a room consistent with that contract;
   * without it a moved-out dai reappears in the room when the bathe screen refreshes the master on
   * "sync from schedule".
   */
  private mapLiveBathDais(rawDais: any[] | null | undefined): BathDais[] {
    return (rawDais || [])
      .filter((dai: any) => !dai.delflg)
      .map((dai: any) => {
        const bathDai = new BathDais();
        bathDai.DAICD = dai.daicd || undefined;
        bathDai.BATHMETHODCD = dai.bathmethodcd || undefined;
        bathDai.BATHWEEKS = dai.bathweeks || undefined;
        bathDai.BATHDAYS = dai.bathdays || undefined;
        bathDai.PRIORITYCD = dai.prioritycd || undefined;
        bathDai.TREATMENTNOTES = dai.treatmentnotes || undefined;
        return bathDai;
      });
  }

  /**
   * Resolve one raw bathroom item from an API response into a typed BathMaster.
   *
   * This is the single point where a stored room becomes the app's model: camelCase keys are
   * mapped to the PascalCase model, and BATHDAIS is reduced to the live roster via mapLiveBathDais
   * (soft-deleted dais dropped). Every endpoint that returns a room routes through here so the
   * "client never sees soft-deleted dais" contract holds uniformly. BathMaster carries DaiTable;
   * consumers that only need BathMasterItem simply ignore it.
   */
  private mapBathMaster(item: any): BathMaster {
    const bathMaster = new BathMaster();
    bathMaster.PartitionKey = item.partitionKey || '';
    bathMaster.SortKey = item.sortKey || '';
    bathMaster.BLOCKCD = item.blockcd || undefined;
    bathMaster.SUBCD = item.subcd || undefined;
    bathMaster.UNITCD = item.unitcd || undefined;
    bathMaster.BATHNAME = item.bathname || undefined;
    bathMaster.CAPACITY = item.capacity || undefined;
    bathMaster.SORTNO = item.sortno ?? undefined;
    bathMaster.BATHRULE = item.bathrule || undefined;
    bathMaster.BATHDAIS = this.mapLiveBathDais(item.bathdais);
    bathMaster.DaiTable = item.daiTable || '';
    bathMaster.INSSTFCD = item.insstfcd || undefined;
    bathMaster.INSDATE = item.insdate || undefined;
    bathMaster.UPDSTFCD = item.updstfcd || undefined;
    bathMaster.UPDDATE = item.upddate || undefined;
    bathMaster.ITEMVER = item.itemver ?? null;
    return bathMaster;
  }

  getBathRoomInfo() {
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.bathRoomSetting.getBathRoomInfo}`;
    const body = {
      corpCd, 
      officeCd
    };
    return this.http.post(url, body);
  }

  getBathDai(bathRoomCd: string, from: string, to: string) {
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.bathRoomSetting.getBathDai}`;
    const body = {
      corpCd,
      bathRoomCd,
      officeCd,
      from,
      to
    };
    return this.http.post(url, body);
  }

  getBathDaily(bathRoomCd: any, from: string, to: string) {
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.bathRoomSetting.getBathDaily}`;
    const body = {
      corpCd,
      bathRoomCd,
      officeCd,
      from,
      to
    };
    return this.http.post(url, body);
  }

  getDuoDailyData(bathRoomCd: any, from: string, to: string) {
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const requestModel: Partial<BathDailyRequestModel> = {
      officeCd: officeCd,
      bathroomUID: bathRoomCd,
      from,
      to
    };
    return forkJoin({
      bathDaily: this.getBathLogs(requestModel),
      bathDai: this.getBathroomSummary()
    });
  }

  insertOrUpdateBathRoomInfo(request:InsertOrUpdateBathRoomModel) {
    const url = `${API_ENDPOINT.bathRoomSetting.insertOrUpdateBathRoom}`;
    const body = request;
    return this.http.post(url, body);
  }

  deleteBath(request:BathRoom) {
    const url = `${API_ENDPOINT.bathRoomSetting.deleteBath}`;
    const body = request;
    return this.http.delete(url, body);
  }

  updateBathDai(request:BathDaiRequestModel[]) {
    const url = `${API_ENDPOINT.bathRoomSetting.updateBathDai}`;
    const body = request;
    return this.http.post(url, body);
  }

  insertBathDaily(request:BathDaiRequestModel[]) {
    const url = `${API_ENDPOINT.bathRoomSetting.insertBathDaily}`;
    const body = request;
    return this.http.post(url, body);
  }

  updateBathDaily(request:BathDaiRequestModel[]) {
    const url = `${API_ENDPOINT.bathRoomSetting.updateBathDaily}`;
    const body = request;
    return this.http.post(url, body);
  }

  deleteAllBathDailyByDate(request: BathDaiRequestModel[]) {
    const url = `${API_ENDPOINT.bathRoomSetting.deleteAllOrtherBathDailyByDate}`;
    const body = request;
    return this.http.post(url, body);
  }

  //#region New endpoints
  getBathroomByKey(pk: string, sk: string) {
    const url = `${API_ENDPOINT.bathRoomSetting.getBathroomByKey}?pk=${encodeURIComponent(pk)}&sk=${encodeURIComponent(sk)}`;
    return this.http.get(url).pipe(
      map((response: any) => {
        if (response?.status === 200 && response?.result) {
          return {
            ...response,
            result: this.mapBathMaster(response.result)
          };
        }
        return response;
      })
    );
  }

  getBathroomSummary() {
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.bathRoomSetting.getBathroomSummary}?OfficeCd=${officeCd}`;
    return this.http.get(url).pipe(
      map((response: any) => {
        if (response?.status === 200 && response?.result) {
          return {
            ...response,
            result: (response.result as any[]).map((item: any) => this.mapBathMaster(item))
          };
        }
        return response;
      })
    );
  }

  createBathroom(request: BathMasterItem) {
    const url = `${API_ENDPOINT.bathRoomSetting.createBathroom}`;
    const body = request;
    return this.http.post(url, body).pipe(
      map((response: any) => {
        if (response?.status === 200 && response?.result) {
          return {
            ...response,
            result: this.mapBathMaster(response.result)
          };
        }
        return response;
      })
    );
  }

  updateBathroom(request: BathMasterItem) {
    const url = `${API_ENDPOINT.bathRoomSetting.updateBathroom}`;
    const body = request;
    return this.http.put(url, body).pipe(
      map((response: any) => {
        if (response?.status === 200 && response?.result) {
          return {
            ...response,
            result: this.mapBathMaster(response.result)
          };
        }
        return response;
      })
    );
  }

  deleteBathroom(request: DeleteBathroom) {
    const url = `${API_ENDPOINT.bathRoomSetting.deleteBathroom}`;
    const body = request;
    return this.http.delete(url, body);
  }

  updateBathdai(request: BathDaiUpdate) {
    const url = `${API_ENDPOINT.bathRoomSetting.updateBathdai}`;
    const body = request;
    return this.http.put(url, body);
  }

  moveBathdai(request: BathDaiMove) {
    const url = `${API_ENDPOINT.bathRoomSetting.moveBathdai}`;
    const body = request;
    return this.http.put(url, body);
  }

  /** Read-only: what a dai edit would do to the existing future days (result: BathDaiChangePreviewResult). */
  previewBathDaiChange(request: BathDaiChangePreviewRequest) {
    const url = `${API_ENDPOINT.bathRoomSetting.previewBathdaiChange}`;
    const body = request;
    return this.http.post(url, body);
  }

  getBathLogs(params: Partial<BathDailyRequestModel>) {
    // Build query string from provided parameters
    const queryParams: string[] = [];

    if (params.corpCd) {
      queryParams.push(`CorpCd=${encodeURIComponent(params.corpCd)}`);
    }
    if (params.officeCd) {
      queryParams.push(`OfficeCd=${encodeURIComponent(params.officeCd)}`);
    }
    if (params.bathroomUID) {
      queryParams.push(`BathroomUID=${encodeURIComponent(params.bathroomUID)}`);
    }
    if (params.from) {
      queryParams.push(`From=${encodeURIComponent(params.from)}`);
    }
    if (params.to) {
      queryParams.push(`To=${encodeURIComponent(params.to)}`);
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const url = `${API_ENDPOINT.bathRoomSetting.bathLogs}${queryString}`;

    return this.http.get(url);
  }

  // suppressSignal=true skips the backend bath-change RTDB signal. Used by the bathing-schedule
  // ISVALID auto-save so its derived write does not trigger an office-wide refresh (feedback loop).
  updateLogs(request: BathDailyItem[], suppressSignal: boolean = false) {
    const url = suppressSignal
      ? `${API_ENDPOINT.bathRoomSetting.updateLogs}?suppressSignal=true`
      : `${API_ENDPOINT.bathRoomSetting.updateLogs}`;
    const body = request;
    return this.http.post(url, body);
  }

  updateDaily(request: DAISWithKeys) {
    const url = `${API_ENDPOINT.bathRoomSetting.updateDaily}`;
    const body = request;
    return this.http.put(url, body);
  }
  //#endregion
}
